"use client";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Coin from "../../assets/stcoin.png";
import Image from "next/image";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import EarnCoinsDetails from "./EarnCoinsDetails";
import StoreCategories from "./StoreCategories";
import { ApiService } from "@/app/services/AppService";
import { ResponseObject } from "@/app/classes/main";
import { getToken } from "@/app/services/HolderService";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import "./index.css";
import { trackEvent, waitForBranch } from "@/app/utils/branch";
import toast from "react-hot-toast";

export const Store = () => {
  const apiService = useMemo(() => new ApiService(), []);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);
  const [openStore, setOpenStore] = useState(false);
  const [subCategoriesData, setSubCategoriesData] = useState({
    data: [],
    name: "",
  });
  const [storeCategoriesData, setStoreCategoriesData] = useState([]);
  const [storeProductBanners, setStoreProductBanners] = useState([]);
  const [token, setToken] = useState<String | null>(null);

  const handleOpenEarnCoins = () => {
    setOpen(true);
  };

  const getStoreSubCategories = async (id: any, name: any) => {
    try {
      const responseObject = await apiService.get<ResponseObject>(
        apiService.storeSubCategories + "cid=" + id,
        getToken()!
      );
      if (responseObject?.status == "success") {
        setSubCategoriesData({ data: responseObject.object, name: name });
        setOpenStore(true);
      }
    } catch (error) {
      console.log("Error :", error);
    }
  };

  const handleOpenStoreCategories = (id: any, name: any) => {
    trackEvent("STORE_PRODUCT_VIEW", id);
    getStoreSubCategories(id, name);
  };

  const getProductOrders = async () => {
    try {
      const responseObject = await apiService.get<ResponseObject>(
        apiService.storeProductOrder,
        getToken()!
      );
      if (responseObject?.status == "success") {
        // setStoreCategoriesData(responseObject.object);
        // console.log("----<>>>>>>>>>", responseObject);
        toast.error("No Order Found", {
          duration: 5000,
          style: { padding: "16px" },
        });
      }
    } catch (error) {
      console.log("Error:", error);
    }
  };

  const getStoreCategories = useCallback(async () => {
    try {
      const responseObject = await apiService.get<ResponseObject>(
        apiService.storeCategories,
        getToken()!
      );
      if (responseObject?.status == "success") {
        setStoreCategoriesData(responseObject.object);
      }
    } catch (error) {
      console.log("Error:", error);
    }
  }, [apiService]);

  const getStoreProductBanners = useCallback(async () => {
    try {
      const responseObject = await apiService.get<ResponseObject>(
        apiService.storeProductsBanners
        // getToken()!
      );
      if (responseObject?.status == "success") {
        setStoreProductBanners(responseObject.object);
      }
    } catch (error) {
      console.log("Error:", error);
    }
  }, [apiService]);

  useEffect(() => {
    getStoreCategories();
    getStoreProductBanners();
  }, [getStoreCategories, getStoreProductBanners]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    setToken(token);
  }, []);

  React.useEffect(() => {
    waitForBranch().then(() => {
      trackEvent("STORE_VIEW", null);
    });
  }, []);

  const mintGradient = "linear-gradient(135deg, #123524 0%, #1f7a4d 100%)";

  return (
    <Box sx={{ pb: { xs: 10, sm: 0 } }}>
      <Grid
        container
        item
        xs={12}
        sm={11}
        md={9.5}
        lg={9.5}
        sx={{
          margin: { xs: "0rem auto", sm: "2rem auto" },
          padding: { xs: "1rem", sm: "1rem 1.25rem" },
          maxWidth: "min(92%, 1220px)",
        }}
      >
        <Box
          sx={{
            display: { xs: "block", sm: "block", md: "flex" },
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            gap: 2,
            borderRadius: 3,
            border: "1px solid rgba(18, 53, 36, 0.1)",
            boxShadow: "0 12px 40px rgba(18, 53, 36, 0.08)",
            background:
              "linear-gradient(135deg, rgba(223, 246, 232, 0.5) 0%, #ffffff 55%, rgba(246, 251, 248, 0.95) 100%)",
            p: { xs: 2, sm: 2.5 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: {
                xs: "flex-start",
                sm: "flex-start",
                md: "left",
                lg: "left",
              },
              padding: "0.25rem 0",
              margin: { xs: "0 0 1rem", sm: "0 0 1rem", md: 0 },
              width: { xs: "100%", sm: "100%", md: "50%" },
            }}
          >
            <Image
              className="cart-coin-animation"
              src={Coin}
              height={500}
              width={500}
              alt="coin"
              style={{
                height: "3rem",
                width: "3rem",
              }}
            />
            <Typography
              variant="body1"
              sx={{
                textAlign: "left",
                color: "#123524",
                fontWeight: 600,
                marginLeft: "1rem",
                lineHeight: 1.45,
              }}
            >
              Use Salontym coins to buy any items from Salontym Store.
            </Typography>
          </Box>
          <Box
            sx={{
              width: { xs: "100%", sm: "100%", md: "50%" },
              textAlign: "center",
            }}
          >
            <Button
              variant="contained"
              endIcon={<KeyboardArrowRightIcon />}
              sx={{
                textTransform: "capitalize",
                background: mintGradient,
                color: "#fff",
                padding: { xs: "0.75rem 1rem", sm: "0.85rem 1.25rem" },
                width: "-webkit-fill-available",
                fontWeight: 600,
                borderRadius: "999px",
                boxShadow: "0 8px 24px rgba(18, 53, 36, 0.2)",
                "&:hover": {
                  background: "linear-gradient(135deg, #0f2d1f 0%, #1a6b42 100%)",
                },
              }}
              onClick={handleOpenEarnCoins}
            >
              How to earn Salontym coins
            </Button>
          </Box>
        </Box>
        <Grid
          sx={{
            margin: { xs: "2rem auto", sm: "4rem auto" },
          }}
          xs={12}
          sm={12}
          md={12}
          lg={12}
          container
        >
          {storeCategoriesData && storeCategoriesData.length > 0
            ? storeCategoriesData?.map((item: any, index: any) => (
                <Grid
                  key={index}
                  margin="auto"
                  xs={6}
                  sm={6}
                  md={4}
                  lg={4}
                  xl={3}
                  item
                  justifyItems="center"
                >
                  <Box
                    sx={{
                      borderRadius: "16px",
                      width: "max-content",
                      border: "1px solid rgba(18, 53, 36, 0.1)",
                      boxShadow: "0 8px 28px rgba(18, 53, 36, 0.08)",
                      margin: {
                        xs: "8px",
                        sm: "8px",
                        md: "8px",
                        lg: "8px 16px",
                      },
                      cursor: "pointer",
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      overflow: "hidden",
                      background: "#fff",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 14px 36px rgba(18, 53, 36, 0.12)",
                      },
                    }}
                    onClick={() =>
                      handleOpenStoreCategories(item.id, item.name)
                    }
                  >
                    <Image
                      src={item?.profileImage?.secureHttpPath ?? ""}
                      width={1000}
                      height={1000}
                      className=""
                      style={{
                        margin: "auto",
                        objectFit: "cover",
                        borderRadius: "15px 15px 0 0",
                        width: isSmallScreen ? "9rem" : "14rem",
                        maxWidth: "15rem",
                        height: isSmallScreen ? "6rem" : "9rem",
                      }}
                      alt="category"
                    />
                    <Box
                      sx={{
                        textWrap: "nowrap",
                        margin: { xs: "8px 4px", sm: "16px 8px" },
                      }}
                    >
                      {" "}
                      <Typography
                        className="text-center text-sm"
                        sx={{
                          fontWeight: "bold",
                          fontSize: { xs: "12px", sm: "14px" },
                        }}
                      >
                        {item?.name}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))
            : Array.from({ length: 4 }).map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  sx={{
                    margin: {
                      xs: "8px",
                      sm: "8px",
                      md: "8px",
                      lg: "0px 15px",
                    },
                    width: { xs: "150px", sm: "14rem" },
                    height: { xs: "150px", sm: "13rem" },
                  }}
                />
              ))}
        </Grid>
        <Box
          className="banner-container"
          sx={{
            display: "-webkit-inline-box",
            justifyContent: { md: "left", lg: "center" },
            alignItems: "center",
            overflowX: "scroll",
            width: "100%",
            margin: "auto",
            backgroundColor: "white",
            borderColor: "none",
          }}
        >
          {storeProductBanners &&
            storeProductBanners.map((item: any, index: any) => (
              <Image
                key={index}
                src={item?.webImage.secureHttpPath}
                width={1000}
                height={1000}
                style={{
                  width: isSmallScreen ? "330px" : "480px",
                  height: isSmallScreen ? "10rem" : "13rem",
                  objectFit: "cover",
                  margin: isSmallScreen ? "0.5rem" : "1rem",
                  borderRadius: "0.5rem",
                  cursor: "pointer",
                }}
                alt="Banner Image"
                onClick={() =>
                  handleOpenStoreCategories(item.categoryId, item.categoryName)
                }
              />
            ))}
        </Box>
      </Grid>
      {token && (
        <Box
          sx={{
            position: "fixed",
            right: { xs: "1rem", sm: "2rem" },
            bottom: { xs: "4.5rem", sm: "2rem" },
            zIndex: 1000,
          }}
        >
          <Button
            variant="contained"
            sx={{
              borderRadius: "999px",
              textTransform: "capitalize",
              padding: "8px 18px",
              background: mintGradient,
              color: "#fff",
              boxShadow: "0 8px 24px rgba(18, 53, 36, 0.25)",
              "&:hover": {
                background: "linear-gradient(135deg, #0f2d1f 0%, #1a6b42 100%)",
              },
            }}
            startIcon={<CalendarMonthIcon />}
            onClick={getProductOrders}
          >
            Orders
          </Button>
        </Box>
      )}
      <EarnCoinsDetails open={open} close={() => setOpen(false)} />
      <StoreCategories
        open={openStore}
        close={() => setOpenStore(false)}
        subCategoriesData={subCategoriesData}
        setSubCategoriesData={() =>
          setSubCategoriesData({
            data: [],
            name: "",
          })
        }
      />
    </Box>
  );
};
